/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { DEV_MODE } from '../lib/dev-stubs';

export type AffiliateDisplayMode = 'inline-card' | 'sticky-banner' | 'tooltip' | 'collapsed';

interface AffiliateDisplayContextValue {
  displayMode: AffiliateDisplayMode;
  setDisplayMode: (mode: AffiliateDisplayMode) => void;
  isDevModeEnabled: boolean;
}

const AffiliateDisplayContext = createContext<AffiliateDisplayContextValue | undefined>(undefined);

interface AffiliateDisplayProviderProps {
  children: React.ReactNode;
}

export function AffiliateDisplayProvider({ children }: AffiliateDisplayProviderProps) {
  const [displayMode, setDisplayMode] = useLocalStorage<AffiliateDisplayMode>(
    'healfm-affiliate-display-mode',
    'inline-card'
  );

  const value = useMemo<AffiliateDisplayContextValue>(
    () => ({
      displayMode,
      setDisplayMode,
      isDevModeEnabled: DEV_MODE,
    }),
    [displayMode, setDisplayMode]
  );

  return (
    <AffiliateDisplayContext.Provider value={value}>
      {children}
    </AffiliateDisplayContext.Provider>
  );
}

export function useAffiliateDisplay(): AffiliateDisplayContextValue {
  const context = useContext(AffiliateDisplayContext);
  if (context === undefined) {
    throw new Error('useAffiliateDisplay must be used within an AffiliateDisplayProvider');
  }
  return context;
}
